import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { getPosts } from "../lib/api";
import { format } from "date-fns";

// Helper function to create excerpt from post body
const createExcerpt = (body: string, maxLength = 150) => {
  if (body.length <= maxLength) return body;
  return body.substring(0, maxLength).trim() + "...";
};

export default function PostsPage() {
  // Get the current URL search params using react-router-dom
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") ?? 1);
  const limit = Number(searchParams.get("limit") ?? 10);

  const {
    data: posts,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["posts", { page, limit }],
    queryFn: () => getPosts(page, limit),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h2 className="text-lg font-semibold">Loading posts...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-red-600">
            Error loading posts
          </h2>
          <p className="mt-2 text-gray-600">Please try again later.</p>
        </div>
      </div>
    );
  }

  const handlePageChange = (newPage: number) => {
    setSearchParams({ page: newPage.toString(), limit: limit.toString() });
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Posts</h1>
        <Link
          to="/app/posts/new"
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          Create Post
        </Link>
      </div>

      <div className="space-y-6">
        {posts?.map((post) => (
          <div key={post._id} className="p-6 rounded-lg shadow">
            <Link
              to={`/app/posts/${post._id}`}
              className="text-xl font-semibold hover:text-indigo-600"
            >
              {post.title}
            </Link>
            <div className="mt-2 text-gray-600">{createExcerpt(post.body)}</div>
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <span>{post.authorEmail}</span>
              <span className="mx-2">•</span>
              <time dateTime={post.createdAt}>
                {format(new Date(post.createdAt), "MMM d, yyyy")}
              </time>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="mt-8 flex justify-center gap-2">
        <button
          onClick={() => handlePageChange(page - 1)}
          className={`px-4 py-2 rounded ${
            page === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-indigo-600 text-white hover:bg-indigo-700"
          }`}
          disabled={page === 1}
        >
          Previous
        </button>
        <button
          onClick={() => handlePageChange(page + 1)}
          className={`px-4 py-2 rounded ${
            !posts || posts.length < limit
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-indigo-600 text-white hover:bg-indigo-700"
          }`}
          disabled={!posts || posts.length < limit}
        >
          Next
        </button>
      </div>
    </div>
  );
}
